import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ElementRef } from '@angular/core';

import { loadStripe, Stripe, StripeCardCvcElement, StripeCardExpiryElement, StripeCardNumberElement, StripeElements } from '@stripe/stripe-js';

import { SessionManager } from '@services/session-manager';
import { SharedService } from '@services/shared.service';

import { environment } from '@environments/environment';


// Resultado de tokenizar la tarjeta. Es lo único que sale de este componente:
// el número de tarjeta se queda en el iframe de Stripe y nunca llega al backend.
export interface IStripeCardToken {
    token: string;
    brand: string;
    last4: string;
}


/**
 * Formulario de tarjeta de Stripe (Stripe Elements).
 *
 * Los campos son iframes servidos por Stripe: el número, la expiración y el CVC
 * nunca tocan nuestro DOM ni nuestro servidor. Al confirmar se devuelve un
 * payment method (pm_xxx), que es lo único que viaja al backend.
 *
 * Por ser iframes tampoco heredan el CSS de la página, por eso los estilos (y el
 * modo oscuro) se les pasan por JavaScript al crearlos.
 */
@Component({
    selector: 'app-stripe-card',
    templateUrl: './stripe-card.component.html',
    styleUrls: ['./stripe-card.component.scss']
})
export class StripeCardComponent implements OnInit, OnDestroy {

    // Los Elements no se re-estilizan solos: si el tema cambia hay que recrearlos.
    @Input() isDarkMode = false;

    // Nombre del titular. Se manda a Stripe como billing details; el backend lo
    // recibe aparte en `cardname`.
    @Input() cardholderName = '';

    // Emite true cuando los tres campos están completos y sin errores
    @Output() completeChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild('cardNumber', { static: true }) cardNumberRef: ElementRef;
    @ViewChild('cardExpiry', { static: true }) cardExpiryRef: ElementRef;
    @ViewChild('cardCvc', { static: true }) cardCvcRef: ElementRef;

    // Errores por campo, para mostrarlos igual que el formulario de ONVO
    numberError = '';
    expiryError = '';
    cvcError = '';

    loading = true;

    private stripe: Stripe = null;
    private elements: StripeElements = null;

    private cardNumberElm: StripeCardNumberElement = null;
    private cardExpiryElm: StripeCardExpiryElement = null;
    private cardCvcElm: StripeCardCvcElement = null;

    private numberComplete = false;
    private expiryComplete = false;
    private cvcComplete = false;

    constructor(public sharedService: SharedService) {
    }

    get isReady(): boolean {

        return this.stripe != null && this.cardNumberElm != null;
    }

    ngOnInit(): void {

        this.mountElements();
    }

    ngOnDestroy(): void {

        // Los iframes no se van con el componente: hay que destruirlos a mano
        this.cardNumberElm?.destroy();
        this.cardExpiryElm?.destroy();
        this.cardCvcElm?.destroy();
    }

    /**
     * Tokeniza la tarjeta con Stripe.js.
     * @returns una Promesa con el payment method (pm_xxx), la marca y los últimos 4
     * dígitos, o null si Stripe rechazó los datos. El error ya queda mostrado en el
     * campo que corresponde.
     */
    async createPaymentMethod(): Promise<IStripeCardToken> {

        if (!this.isReady) {

            this.numberError = 'No se pudo cargar el formulario de pago, por favor recargá la página.';
            return null;
        }

        const billingName = this.cardholderName?.trim();

        const { paymentMethod, error } = await this.stripe.createPaymentMethod({
            type: 'card',
            card: this.cardNumberElm,
            billing_details: billingName ? { name: billingName } : undefined
        });

        if (error) {

            this.numberError = this.sharedService.translateStripeError(error.message || '');
            return null;
        }

        return {
            token: paymentMethod.id,
            brand: paymentMethod.card?.brand,
            last4: paymentMethod.card?.last4
        };
    }

    private async mountElements(): Promise<void> {

        // la cuenta de Stripe es una sola. En demo se tokeniza con la key de test, que es la que empata
        // con el secret key de test que usa el backend cuando ve X-TESTMODE.
        const stpPK = SessionManager.isDemoMode() ? environment.stpPKTest : environment.stpPK;

        this.stripe = await loadStripe(stpPK);

        if (!this.stripe) {

            this.loading = false;
            this.numberError = 'No se pudo cargar el formulario de pago, por favor recargá la página.';
            return;
        }

        this.elements = this.stripe.elements();

        const style = this.getElementsStyle();

        // disableLink apaga el botón "Utilizar" de Stripe Link (autocompletado de tarjetas
        // guardadas en Stripe). El checkout ya tiene su propio manejo de tarjetas guardadas.
        this.cardNumberElm = this.elements.create('cardNumber', { style, placeholder: 'Número de tarjeta', disableLink: true });
        this.cardExpiryElm = this.elements.create('cardExpiry', { style, placeholder: 'MM/AA' });
        this.cardCvcElm = this.elements.create('cardCvc', { style, placeholder: 'CVC' });

        this.cardNumberElm.mount(this.cardNumberRef.nativeElement);
        this.cardExpiryElm.mount(this.cardExpiryRef.nativeElement);
        this.cardCvcElm.mount(this.cardCvcRef.nativeElement);

        this.cardNumberElm.on('change', event => {

            this.numberComplete = event.complete;
            this.numberError = this.getFieldError(event, 'El número de tarjeta está incompleto');
            this.emitComplete();
        });

        this.cardExpiryElm.on('change', event => {

            this.expiryComplete = event.complete;
            this.expiryError = this.getFieldError(event, 'La fecha de expiración está incompleta');
            this.emitComplete();
        });

        this.cardCvcElm.on('change', event => {

            this.cvcComplete = event.complete;
            this.cvcError = this.getFieldError(event, 'El CVC está incompleto');
            this.emitComplete();
        });

        this.loading = false;
    }

    // Un campo vacío no se marca en rojo: el botón de pagar ya queda deshabilitado
    // por completeChange, igual que el formulario de ONVO valida contra `touched`.
    private getFieldError(event: { error?: { message?: string }, empty: boolean, complete: boolean }, incompleteMsg: string): string {

        if (event.error) return this.sharedService.translateStripeError(event.error.message || '');
        if (event.empty) return '';
        if (!event.complete) return incompleteMsg;

        return '';
    }

    private emitComplete(): void {

        this.completeChange.emit(this.numberComplete && this.expiryComplete && this.cvcComplete);
    }

    // Los iframes de Stripe no ven el CSS de la página, así que la tipografía y el
    // color del texto se les pasan acá. El fondo se omite a propósito: el iframe queda
    // transparente y se ve la caja del .scss, que es la que replica al input de la app.
    // lineHeight 30px = 54 de alto - 22 de padding - 2 de borde, para que el texto
    // quede centrado igual que en los demás campos.
    private getElementsStyle(): any {

        return {
            base: {
                color: this.isDarkMode ? '#ffffff' : '#000000',
                fontSize: '16px',
                fontFamily: 'Inter, sans-serif',
                lineHeight: '30px',
                '::placeholder': {
                    color: '#6c757d'
                }
            },
            invalid: {
                color: '#dc3545',
                iconColor: '#dc3545'
            }
        };
    }
}
